import { Component, computed, effect, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { ContentScoreSummaryDataResponse } from 'dashboard/components/visuals-dashboard/widgets/content-score-summary-widget/content-score-summary-data-response.interface';
import { IcePieRangesToDashboardPieRangesColorMap } from 'dashboard/constants/visuals-dashboard/ice-ranges-to-dashboard-ranges-colors-map';
import { IceToDashboardColorMapRanges } from 'dashboard/constants/visuals-dashboard/ice-to-dashboard-colors-map';
import { IcePieRangesColorCodesEnum } from 'dashboard/enums/visuals/ice-ranges-color-codes.enum';
import { VisualsDashboardRangesColorEnum } from 'dashboard/enums/visuals/visuals-dashboard-colors.enum';
import { VisualsDashboardWidgetSizingEnum } from 'dashboard/enums/visuals/visuals-dashboard-widget-sizing.enum';
import { DashboardWidget } from 'dashboard/interfaces/visuals/dashboard-widget.interface';
import { VisualsDashboardDataService } from 'dashboard/services/visuals-dashboard/visualds-dashboard-data.service';
import { camelCase } from 'lodash-es';
import {
    HorizontalBarChartBar,
    IceVisualsDashboardHorizontalBarChartComponent,
} from 'shared/components/ice-visuals-dashboard/ice-visuals-dashboard-horizontal-bar-chart/ice-visuals-dashboard-horizontal-bar-chart.component';
import {
    IceVisualsDashboardPieChartComponent,
    PieChartCenterLabel,
    PieChartSegment,
    PieChartVariantEnum,
} from 'shared/components/ice-visuals-dashboard/ice-visuals-dashboard-pie-chart/ice-visuals-dashboard-pie-chart.component';

const COMBINED_LABEL_KEY = 'COMBINED';
@Component({
    selector: 'app-content-score-summary-widget',
    imports: [IceVisualsDashboardHorizontalBarChartComponent, IceVisualsDashboardPieChartComponent],
    templateUrl: './content-score-summary-widget.component.html',
    styleUrl: './content-score-summary-widget.component.scss',
    providers: [VisualsDashboardDataService],
})
export class ContentScoreSummaryWidgetComponent {
    widget = input.required<DashboardWidget>();
    visualsDashboardWidgetSizingEnum = VisualsDashboardWidgetSizingEnum;
    pieChartVariantEnum = PieChartVariantEnum;
    onLangChange = toSignal(this.translate.onLangChange);

    selectedSize = computed(() => {
        const widget = this.widget();
        return widget.selectedSize;
    });

    widgetId = computed(() => {
        const widget = this.widget();
        return widget.iceDashboardWidget.widgetId;
    });

    score = computed(() => {
        const widgetData = this.dataService.data();
        return widgetData ? widgetData.summaryScore : 0;
    });

    contentScoreCenterLabel = computed(() => {
        const data = this.dataService.data();
        this.onLangChange(); // Re-compute when language changes
        const returnValue: PieChartCenterLabel = {
            title: this.translate.instant('dashboard.visuals.components.scoreDisplay.yourScore'),
            value: data ? data.summaryScore : 0,
            subtitle: this.translate.instant('dashboard.visuals.components.scoreDisplay.outOfTotal', { totalScore: 100 }),
        };
        return returnValue;
    });

    contentScoreSegments = computed(() => {
        const score = this.score();
        this.onLangChange();
        const segments: PieChartSegment[] = [
            {
                label: this.translate.instant('dashboard.visuals.widgets.contentScoreSummary.pieChart.score'),
                value: score,
                color: this.getScoreColorByScore(score),
            },
            {
                label: '',
                value: 100 - score,
                color: '#E5E7EB',
            },
        ];

        return segments;
    });

    getScoreColorByScore(score: number): VisualsDashboardRangesColorEnum {
        let colorCode: IcePieRangesColorCodesEnum;
        switch (true) {
            case score === 100:
                colorCode = IcePieRangesColorCodesEnum.RANGE_100;
                break;
            case score >= 80:
                colorCode = IcePieRangesColorCodesEnum.RANGE_80_99;
                break;
            case score >= 60:
                colorCode = IcePieRangesColorCodesEnum.RANGE_60_79;
                break;
            case score >= 40:
                colorCode = IcePieRangesColorCodesEnum.RANGE_40_59;
                break;
            case score >= 20:
                colorCode = IcePieRangesColorCodesEnum.RANGE_20_39;
                break;
            default:
                colorCode = IcePieRangesColorCodesEnum.RANGE_0_19;
                break;
        }
        const mappedColor = IcePieRangesToDashboardPieRangesColorMap[colorCode as keyof typeof IcePieRangesToDashboardPieRangesColorMap];
        return mappedColor;
    }

    contentScoreBars = computed(() => {
        const widgetData = this.dataService.data();
        if (!widgetData) {
            return [];
        }
        return widgetData.ranges
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((range) => {
                const mappedColor = IceToDashboardColorMapRanges[range.colorCode as keyof typeof IceToDashboardColorMapRanges];
                const camelCaseTitleKey = camelCase(range.title);
                const bar: HorizontalBarChartBar = {
                    label: `dashboard.visuals.widgets.contentScoreSummary.barChart.labels.${camelCaseTitleKey}`,
                    value: range.displayValue,
                    targetValue: range.compareToValue,
                    color: mappedColor || range.colorCode,
                    isCombinedBar: range.title.toUpperCase() === COMBINED_LABEL_KEY,
                };
                return bar;
            });
    });

    constructor(
        public dataService: VisualsDashboardDataService<ContentScoreSummaryDataResponse>,
        private translate: TranslateService,
    ) {
        effect(() => {
            const widgetId = this.widgetId();
            this.dataService.load(widgetId);
        });
    }
}
